// src/pages/ELearning/KPIPage.tsx — ФИЧА R: KPI целевые показатели
import React, { useEffect, useState } from 'react';
import { getKPIProgress, createKPI, deleteKPI } from '../../api/client';
import { Loader2, Target, Plus, Trash2, TrendingUp } from 'lucide-react';

const METRIC_LABELS: Record<string, string> = {
  tests_completed: 'Пройдено тестов',
  tests_passed: 'Успешных тестов',
  avg_score: 'Средний балл (%)',
  points_earned: 'Баллов заработано',
  mandatory_completed: 'Обязательных пройдено',
  streak_days: 'Дней в серии',
};

const TARGET_LABELS: Record<string, string> = {
  company: 'Вся компания',
  department: 'Отдел',
  role: 'Роль',
  user: 'Сотрудник',
};

const PERIOD_LABELS: Record<string, string> = {
  week: 'Неделя',
  month: 'Месяц',
  quarter: 'Квартал',
  year: 'Год',
};

const KPIPage: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', targetType: 'company', targetValue: '', metric: 'tests_completed', target: 10, period: 'month' });

  const load = async () => {
    setLoading(true);
    try {
      const d = await getKPIProgress();
      setData(d);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.name || !form.target) { alert('Заполните имя и цель'); return; }
    try {
      await createKPI(form);
      setShowCreate(false);
      setForm({ name: '', targetType: 'company', targetValue: '', metric: 'tests_completed', target: 10, period: 'month' });
      load();
    } catch (e: any) { alert('Ошибка: ' + (e.response?.data?.error || e.message)); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Удалить KPI?')) return;
    try { await deleteKPI(id); load(); } catch (e) {}
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Loader2 size={40} color="#667eea" style={{ animation: 'spin 1s linear infinite' }} /></div>;

  const items = data?.items || [];

  return (
    <>
      <style>{`@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } } .kpi-card { animation: fadeUp 0.5s ease-out backwards; }`}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, animation: 'fadeUp 0.4s ease-out' }}>
        <div style={{ width: 56, height: 56, borderRadius: 18, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 32px rgba(17,153,142,0.4)' }}>
          <Target size={28} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, background: 'linear-gradient(135deg, #1a1a2e 0%, #11998e 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>KPI обучения</h1>
          <p style={{ color: '#8b8fa8', margin: '4px 0 0', fontSize: 14, fontWeight: 500 }}>Целевые показатели и прогресс</p>
        </div>
        <button onClick={() => setShowCreate(true)} style={{ padding: '12px 20px', background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#fff', border: 'none', borderRadius: 14, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, boxShadow: '0 8px 24px rgba(17,153,142,0.3)' }}>
          <Plus size={18} /> Создать KPI
        </button>
      </div>

      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: 'rgba(255,255,255,0.5)', borderRadius: 24, border: '1px dashed rgba(102,126,234,0.3)' }}>
          <Target size={48} color="#667eea" style={{ opacity: 0.4 }} />
          <h3 style={{ color: '#5a5f7a', marginTop: 12, fontWeight: 700 }}>Нет активных KPI</h3>
          <p style={{ color: '#8b8fa8', fontSize: 13 }}>Создайте первый целевой показатель</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          {items.map((item: any, i: number) => {
            const k = item.kpi;
            const progress = item.progress;
            const statusColor = item.status === 'achieved' ? '#10b981' : item.status === 'on_track' ? '#667eea' : '#ef4444';
            const statusGradient = item.status === 'achieved'
              ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
              : item.status === 'on_track'
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
            return (
              <div key={k._id} className="kpi-card" style={{ background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.7)', borderRadius: 22, padding: 20, boxShadow: '0 8px 32px rgba(31,38,135,0.08)', animationDelay: `${i * 0.05}s` }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a2e', marginBottom: 4 }}>{k.name}</div>
                    <div style={{ fontSize: 12, color: '#8b8fa8', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <span>🎯 {METRIC_LABELS[k.metric] || k.metric}</span>
                      <span>👥 {TARGET_LABELS[k.targetType]}{k.targetValue ? ': ' + k.targetValue : ''}</span>
                      <span>📅 {PERIOD_LABELS[k.period]}</span>
                    </div>
                  </div>
                  <button onClick={() => handleDelete(k._id)} style={{ padding: 8, background: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 10, color: '#ef4444' }}><Trash2 size={16} /></button>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 32, fontWeight: 800, background: statusGradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1 }}>{item.current}</span>
                  <span style={{ fontSize: 14, color: '#8b8fa8', fontWeight: 600 }}>из {item.target}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 14, fontWeight: 800, color: statusColor }}>{progress}%</span>
                </div>
                <div style={{ height: 8, background: 'rgba(0,0,0,0.06)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${progress}%`, background: statusGradient, borderRadius: 4, transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 }} onClick={() => setShowCreate(false)}>
          <div style={{ background: '#fff', borderRadius: 24, padding: 28, maxWidth: 520, width: '100%' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>🎯 Новый KPI</h3>
            <input placeholder="Название KPI" value={form.name} onChange={e => setForm({...form, name: e.target.value})} style={styles.input} />
            <select value={form.metric} onChange={e => setForm({...form, metric: e.target.value})} style={styles.input}>
              {Object.entries(METRIC_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={form.targetType} onChange={e => setForm({...form, targetType: e.target.value})} style={styles.input}>
              {Object.entries(TARGET_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            {form.targetType !== 'company' && (
              <input placeholder="Значение (отдел/роль/ID)" value={form.targetValue} onChange={e => setForm({...form, targetValue: e.target.value})} style={styles.input} />
            )}
            <input type="number" placeholder="Целевое значение" value={form.target} onChange={e => setForm({...form, target: +e.target.value})} style={styles.input} />
            <select value={form.period} onChange={e => setForm({...form, period: e.target.value})} style={styles.input}>
              {Object.entries(PERIOD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setShowCreate(false)} style={{ padding: '10px 18px', background: 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 12, fontWeight: 700, cursor: 'pointer' }}>Отмена</button>
              <button onClick={handleCreate} style={{ padding: '10px 18px', background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700, cursor: 'pointer' }}>Создать</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  input: { width: '100%', padding: '12px 16px', borderRadius: 12, border: '1px solid rgba(0,0,0,0.1)', fontSize: 14, marginBottom: 12, boxSizing: 'border-box', fontFamily: 'inherit' },
};

export default KPIPage;
